import { Box, Card, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import { isFeatureEnabled } from '@brainpool/utils';
import { Team, Tech, FitScore as FitScoreIcon } from '@brainpool/icon-pack';
import FitScore from '../FitScore/FitScore';
import FitScoreNotification from '../FitScoreNotification/FitScoreNotification';
import { Enrichments } from '../../types/Enrichments';
import { Widget } from '../../types/Widget';

export interface OverviewWidgetProps {
  widget: Widget;
  enrichments?: Enrichments;
  loading: boolean;
  role?: string;
  risks?: number;
  survey_responses: Record<string, any> | undefined;
}

const widgetTitles: Record<string, string> = {
  widget_fitscoreoutput: 'Fit Score',
  widget_sectoroutput: 'Sector',
  widget_teamoutput: 'Team',
  widget_uspoutput: 'USP',
  widget_techoutput: 'Tech',
  widget_keyrisksoutput: 'Key Risks',
  widget_overviewoutput: 'Company Overview',
  widget_widgetquestionsoutput: 'RECOMMENDED QUESTIONS FOR FOUNDERS',
};

export const allowedWidgetIds = [
  'widget_fitscoreoutput',
  'widget_sectoroutput',
  'widget_teamoutput',
  'widget_uspoutput',
  'widget_techoutput',
  'widget_keyrisksoutput',
  'widget_overviewoutput',
  'widget_widgetquestionsoutput',
];

function WidgetHeader({
  id,
  enrichments,
  risks,
}: {
  id: string;
  enrichments?: Enrichments;
  risks?: number;
}) {
  const theme = useTheme();
  switch (id?.toLowerCase()) {
    case 'widget_fitscoreoutput':
      return <FitScore score={enrichments?.fit_score?.score || 0} isThesisFit={true} />;
    case 'widget_techoutput':
      return <Tech size={24} fill={theme.palette.error.dark} />;
    case 'widget_teamoutput':
      return <Team size={24} fill={theme.palette.success.main} />;
    case 'widget_keyrisksoutput':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {risks && (
            <Chip
              label={`Risk: ${risks}`}
              sx={{ mr: 1, bgcolor: 'error.light', color: 'error.dark' }}
            />
          )}
          <FitScoreIcon size={24} fill={theme.palette.error.dark} />
        </Box>
      );
    default:
      return null;
  }
}

export function OverviewWidget({
  widget,
  enrichments,
  role,
  loading = true,
  risks,
  survey_responses,
}: OverviewWidgetProps) {
  const { id, name, data } = widget;
  const widgetData = typeof data === 'string' ? JSON.parse(data) : data;
  let noWidgetData = false;

  if (loading || !widgetData) {
    return (
      <Skeleton variant="rounded" sx={{ height: '300px', width: '100%', marginBottom: '1rem' }} />
    );
  }

  const getTags = (type: string) => {
    let tagArray: string[] = [];
    switch (type) {
      case 'widget_techoutput':
        tagArray = enrichments?.key_technologies?.split(',').map(tech => tech.trim()) || [];
        return tagArray.map(tech => (
          <Chip key={tech} label={tech} sx={{ bgcolor: 'info.light', m: 1, ml: 0 }} />
        ));
      case 'widget_sectoroutput':
        return <Chip label={enrichments?.sector} sx={{ bgcolor: 'info.light', m: 1, ml: 0 }} />;
      default:
        return null;
    }
  };
  const excludeWidgets = ['Unable to generate usp overview.'];
  const widgetOverview =
    widgetData.tech_overview ||
    widgetData.team_overview ||
    widgetData.usp_overview ||
    widgetData.sector_overview;

  if (
    !allowedWidgetIds.includes(widget.id?.toLowerCase()) &&
    !isFeatureEnabled('VITE_FEATURE_FLAG_EXPERIMENTAL_WIDGETS')
  )
    return null;
  if (excludeWidgets.includes(widgetOverview)) {
    noWidgetData = true;
  }
  return (
    <>
      {id?.toLowerCase() === 'widget_fitscoreoutput' && !survey_responses && role === 'Owner' ? (
        <FitScoreNotification survey_responses={survey_responses} />
      ) : (
        <Card
          data-testid="overview-OverviewWidget-card"
          sx={{
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h3">
              {(name || widgetTitles[id?.toLowerCase()] || 'Widget').toUpperCase()}
            </Typography>
            <Box>
              <WidgetHeader id={id} enrichments={enrichments} risks={risks} />
            </Box>
          </Box>
          {getTags(widget.id?.toLowerCase())}

          {noWidgetData ? (
            <Typography variant="body1" color="text.secondary">
              No data available
            </Typography>
          ) : (
            Object.entries(widgetData).map(([key, value]) => {
              if (key !== 'id') {
                return (
                  <Box key={key} sx={{ mb: 2 }}>
                    {key !== 'company_overview' && (
                      <Typography variant="h4" sx={{ mb: 1, textTransform: 'uppercase' }}>
                        {key.replace(/_/g, ' ')}
                      </Typography>
                    )}

                    {Array.isArray(value) ? (
                      value.length > 0 ? (
                        <Box component="ul" sx={{ pl: 2 }}>
                          {value.map((item, index) => (
                            <Typography key={index} component="li" variant="body1" sx={{ mb: 0.5 }}>
                              {item}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">No data available</Typography>
                      )
                    ) : (
                      <Typography variant="body1">{String(value)}</Typography>
                    )}
                  </Box>
                );
              }
              return null;
            })
          )}
        </Card>
      )}
    </>
  );
}
export default OverviewWidget;
